import { defineStore } from 'pinia'
import { getUserInfo, setUserInfo, getToken, setToken, removeToken } from '../utils/storage'
import { userGetInfo } from '../api/models/user'
import { API_CONFIG } from '../config/index.js'
import defaultAvatar from '../static/icons/avatar.webp'

export const useUserStore = defineStore('user', {
  state: () => ({
    userInfo: getUserInfo(),
    token: getToken(),
    isLoading: false,
    personalSettings: {
      theme: 'light',
      notifications: {
        systemMessages: true
      },
      privacy: {
        showPhone: true,
        showEmail: true
      },
      language: 'zh-CN'
    }
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    userName: (state) => state.userInfo.pet_name || state.userInfo.real_name || state.userInfo.account || '',
    account: (state) => state.userInfo.account || '',
    phoneNumber: (state) => state.userInfo.phone || '',
    userId: (state) => state.userInfo.user_id || '',
    userRole: (state) => state.userInfo.user_role || '',
    userAvatar: (state) => state.userInfo.avatar || defaultAvatar,
    gender: (state) => state.userInfo.sex === 0 ? '男' : state.userInfo.sex === 1 ? '女' : '未知',
    email: (state) => state.userInfo.email || '',
    createTime: (state) => state.userInfo.create_time ? new Date(state.userInfo.create_time * 1000).toISOString() : '',
    brandId: (state) => state.userInfo.brand_id || '',
    brandName: (state) => state.userInfo.brand_name || '',
    storeId: (state) => state.userInfo.store_id || '',
    storeName: (state) => state.userInfo.store_name || '',
    totalBalance: (state) => state.userInfo.total_balance || 0,
    withdrawBalance: (state) => state.userInfo.withdraw_balance || 0,
    withdrawEnabled: (state) => state.userInfo.withdraw_enabled || false,
    signPoints: (state) => state.userInfo.sign_points || 0,
    inviterPoints: (state) => state.userInfo.inviter_points || 0,
    inviteCode: (state) => state.userInfo.invite_code || '',
    theme: (state) => state.personalSettings.theme,
    notifications: (state) => state.personalSettings.notifications,
    privacy: (state) => state.personalSettings.privacy
  },

  actions: {
    setUserInfo(data) {
      const processedData = {
        ...data,
        user_id: data.user_id,
        account: data.account,
        phone: data.phone,
        user_role: data.user_role,
        pet_name: data.pet_name,
        real_name: data.real_name,
        sex: data.sex ? Number(data.sex) : 0,
        email: data.email,
        avatar: data.avatar,
        create_time: data.create_time,
        update_time: data.update_time,
        brand_id: data.brand_id,
        brand_name: data.brand_name,
        store_id: data.store_id,
        store_name: data.store_name,
        total_balance: data.total_balance,
        withdraw_balance: data.withdraw_balance,
        withdraw_enabled: data.withdraw_enabled,
        sign_points: data.sign_points,
        inviter_points: data.inviter_points,
        invite_code: data.invite_code
      }

      this.userInfo = { ...this.userInfo, ...processedData }
      setUserInfo(this.userInfo)
    },

    setToken(token) {
      this.token = token
      setToken(token)
    },

    loginSuccess(data) {
      this.setToken(data.token)
      this.setUserInfo(data)
    },

    logout() {
      this.userInfo = {}
      this.token = ''
      this.personalSettings = {
        theme: 'light',
        notifications: {
          systemMessages: true
        },
        privacy: {
          showPhone: true,
          showEmail: true
        },
        language: 'zh-CN'
      }
      removeToken()
      setUserInfo({})
    },

    updateUserInfo(data) {
      this.userInfo = { ...this.userInfo, ...data}
      setUserInfo(this.userInfo)
    },

    async refreshUserInfo() {
      console.log('刷新用户信息')
      try {
        this.isLoading = true
        const res = await userGetInfo()
        if (res.code === 200) {
          let userData = { ...res.data }

          if (userData.avatar) {
            userData.avatar = API_CONFIG.MEDIA_URL + userData.avatar
          }

          this.setUserInfo(userData)
        }
        return res
      } catch (error) {
        console.error('刷新用户信息失败:', error)
        return null
      } finally {
        this.isLoading = false
      }
    },

    updatePersonalSettings(settings) {
      this.personalSettings = { ...this.personalSettings, ...settings }
    },

    updateTheme(theme) {
      this.personalSettings.theme = theme
      if (theme === 'dark') {
        document.documentElement.classList.add('dark-theme')
      } else {
        document.documentElement.classList.remove('dark-theme')
      }
    },

    updateNotificationSettings(notificationSettings) {
      this.personalSettings.notifications = {
        ...this.personalSettings.notifications,
        ...notificationSettings
      }
    },

    updatePrivacySettings(privacySettings) {
      this.personalSettings.privacy = {
        ...this.personalSettings.privacy,
        ...privacySettings
      }
    }
  }
})
